import type {
	AspectRatio,
	PromptMode,
	QualityTier,
} from "@pluck/api/lib/board/prompt-plan";
import type { Edge, Node } from "@xyflow/react";

// Shared with the API (server-side runs for agents).
// biome-ignore lint/performance/noBarrelFile: keeps Studio's import path stable
export {
	ASPECT_RATIOS,
	type AspectRatio,
	AUTO_MODEL,
	collectPromptInputs,
	effectivePromptMode,
	effectivePromptModel,
	LOCAL_MODEL,
	type PromptInput,
	type PromptMode,
	type QualityTier,
} from "@pluck/api/lib/board/prompt-plan";

export interface PluckNodeData extends Record<string, unknown> {
	pluckId: string;
}

export interface PromptNodeData extends Record<string, unknown> {
	/** Hosted runs: output aspect ratio; unset/"auto" = template default. */
	aspect?: AspectRatio;
	/** Unset until the user picks; then `effectivePromptMode` guesses. */
	mode?: PromptMode;
	/** "local" (Codex in Pluck Desktop), "auto", or a hosted model id. */
	model?: string;
	/** Hosted runs: quality tier; unset = the model's default. */
	quality?: QualityTier;
	text: string;
}

/** How many generation cards feed a prompt (the mode only matters for 2+). */
export function generationInputsFor(
	nodes: Node[],
	edges: Edge[],
	promptNodeId: string
): number {
	const sources = new Set(
		edges.filter((e) => e.target === promptNodeId).map((e) => e.source)
	);
	return nodes.filter((n) => sources.has(n.id) && isGenerationNode(n)).length;
}

export interface GenerationNodeData extends Record<string, unknown> {
	generationId: string;
}

export interface ImageNodeData extends Record<string, unknown> {
	uploadId: string;
}

export type PluckNode = Node<PluckNodeData, "pluck">;
export type PromptNode = Node<PromptNodeData, "prompt">;
export type GenerationNode = Node<GenerationNodeData, "generation">;
export type ImageNode = Node<ImageNodeData, "image">;
export type BoardNode = PluckNode | PromptNode | GenerationNode | ImageNode;

export const PLUCK_NODE_WIDTH = 260;
export const PROMPT_NODE_WIDTH = 320;
export const GENERATION_NODE_WIDTH = 420;
export const IMAGE_NODE_WIDTH = 260;
const NODE_GAP = 60;

export function isPluckNode(node: Node): node is PluckNode {
	return node.type === "pluck";
}

export function isPromptNode(node: Node): node is PromptNode {
	return node.type === "prompt";
}

export function isGenerationNode(node: Node): node is GenerationNode {
	return node.type === "generation";
}

export function isImageNode(node: Node): node is ImageNode {
	return node.type === "image";
}

let counter = 0;

/** Ids only need to be unique within a board; a time + counter pair is enough. */
export function nextNodeId(prefix: string): string {
	counter += 1;
	return `${prefix}-${Date.now().toString(36)}-${counter.toString(36)}`;
}

export function createPluckNode(
	pluckId: string,
	position: { x: number; y: number }
): PluckNode {
	return {
		id: nextNodeId("pluck"),
		type: "pluck",
		position,
		data: { pluckId },
	};
}

export function createPromptNode(position: {
	x: number;
	y: number;
}): PromptNode {
	return {
		id: nextNodeId("prompt"),
		type: "prompt",
		position,
		data: { text: "" },
	};
}

export function createGenerationNode(
	generationId: string,
	position: { x: number; y: number }
): GenerationNode {
	return {
		id: nextNodeId("gen"),
		type: "generation",
		position,
		data: { generationId },
	};
}

export function createImageNode(
	uploadId: string,
	position: { x: number; y: number }
): ImageNode {
	return {
		id: nextNodeId("image"),
		type: "image",
		position,
		data: { uploadId },
	};
}

export function createEdge(source: string, target: string): Edge {
	return { id: `e-${source}-${target}`, source, target };
}

/** Position for a node placed to the right of `anchor`. */
export function positionRightOf(
	anchor: Node,
	anchorWidth: number
): { x: number; y: number } {
	return {
		x: anchor.position.x + anchorWidth + NODE_GAP,
		y: anchor.position.y,
	};
}

/** Vertical spacing between generation cards produced by the same prompt. */
export const GENERATION_ROW_HEIGHT = 380;

/** How many generation cards a prompt already feeds. */
export function generationCardsFor(
	nodes: Node[],
	edges: Edge[],
	promptNodeId: string
): number {
	const targets = new Set(
		edges.filter((e) => e.source === promptNodeId).map((e) => e.target)
	);
	return nodes.filter((n) => targets.has(n.id) && isGenerationNode(n)).length;
}

/** Cards stack below the prompt's earlier generations instead of overlapping. */
export function generationSlotPosition(
	promptNode: Node,
	slot: number
): { x: number; y: number } {
	const origin = positionRightOf(promptNode, PROMPT_NODE_WIDTH);
	return { x: origin.x, y: origin.y + slot * GENERATION_ROW_HEIGHT };
}

/** Lay out several new nodes in a column starting at `origin`. */
export function stackPositions(
	origin: { x: number; y: number },
	count: number,
	rowHeight: number
): { x: number; y: number }[] {
	const positions: { x: number; y: number }[] = [];
	for (let i = 0; i < count; i += 1) {
		positions.push({
			x: origin.x,
			y: origin.y + i * (rowHeight + NODE_GAP / 2),
		});
	}
	return positions;
}

/**
 * A connection is valid when it goes pluck, image or generation → prompt (as
 * a reference), or prompt → generation.
 */
export function isValidConnection(
	nodes: Node[],
	source: string | null,
	target: string | null
): boolean {
	if (!(source && target) || source === target) {
		return false;
	}
	const byId = new Map(nodes.map((n) => [n.id, n]));
	const from = byId.get(source);
	const to = byId.get(target);
	if (!(from && to)) {
		return false;
	}
	const isReference =
		isPluckNode(from) || isImageNode(from) || isGenerationNode(from);
	if (isReference && isPromptNode(to)) {
		return true;
	}
	return isPromptNode(from) && isGenerationNode(to);
}
